<script setup>
import { ref, reactive, computed, watch, nextTick, provide } from 'vue';
import { useRoute } from 'vue-router';
import { api } from '/app/plugins/services';
import { sentry } from '/app/plugins/sentry';
import { track } from '/app/utils/analytics';
import { usePlatform } from '/app/composables/platform';
import { useModerationContext } from '/app/composables/moderation';
import { useBus } from '/app/composables/bus';
import { createPointsBubble } from '/app/composables/points-bubble';
import { nudgeGameDash, NUDGE } from '/app/composables/gamedash-nudge';
import { clone } from '/app/utils/helpers';
import { set, get } from 'lodash';
import debounce from 'lodash/debounce';
import Schema from '/app/utils/challenge-schema';
import Storage from '/app/utils/storage';
import Init from '/app/components/challenge/Init.vue';
import QqForm from '/app/components/challenge/QQForm.vue';
import TestIdeaForm from '/app/components/challenge/TestIdeaForm.vue';
import ChallengeForm from '/app/components/challenge/ChallengeForm.vue';
import ImproveIdeaForm from '/app/components/challenge/ImproveIdeaForm.vue';
import InnervationTools from '/app/components/challenge/InnervationTools.vue';
import SubplatformNotice from '/app/components/challenge/SubplatformNotice.vue';
import NIcon from '/app/components/NIcon.vue';
import NDataForm from '/app/components/form/Form.vue';

const CHALLENGE_DRAFT = 'challenge:creation-flow:current-draft';
const defaultDraft = { content: clone(Schema, {}) };

const route = useRoute();
const { contextPlatform } = usePlatform();
const { abilities } = useModerationContext();
const { on, emit: bus } = useBus();

const form = ref(null);
const target = ref(null);

// Anchored on the "Publish & Share" button by Overview.vue, so the award reads
// as coming out of the action that earned it.
const publishBubble = createPointsBubble({
    fqdn: 'catalyst.ChallengePublished.CreatedChallenge',
    unit: 'Neurons',
    color: 'catalyst',
});

// small fix to old drafts
const old = Storage.get(CHALLENGE_DRAFT, {});
if (!old.content) {
    Storage.remove(CHALLENGE_DRAFT);
}

const draft = Storage.get(CHALLENGE_DRAFT, defaultDraft);

const fields = ref(clone(draft.content || {}, Schema));
const categoryTree = ref([]);
const context = reactive({
    categoryMain: null,
    categorySelected: null,
});
const dirtable = [
    'challenge.assumption_what',
    'challenge.assumption_who',
    'challenge.assumption_why',
    'challenge.primary_image',
    'challenge.question_title',
    'challenge.question_detail',
    'challenge.tags',
    'idea.description',
    'idea.title',
];

const isQQ = computed(() => usingTool('qq'));
const isTestIdea = computed(() => usingTool('test-an-idea'));
const isCaption = computed(() => category.value && category.value.id.includes('caption'));
const category = computed(() => categoryTree.value[categoryTree.value.length - 1]);


// --- Methods ---

function usingTool(tool = null) {
    if (!tool) {
        return fields.value.challenge && fields.value.solution_format !== null;
    }
    return fields.value.challenge && fields.value.solution_format === tool;
}

function init() {
    reset({ form: true });
    recoverLastDraft();

    setTimeout(() => {
        if (route.name === 'challenges.test-idea' && fields.value.solution_format !== 'test-an-idea') {
            fields.value.solution_format = 'test-an-idea';
        } else if (fields.value.solution_format === 'test-an-idea') {
            fields.value.solution_format = null;
        }
    }, 300);
}

function reset({ store, form: resetForm, fields: resetFields = true } = {}) {
    if (store) {
        Storage.remove(CHALLENGE_DRAFT);
    }

    if (resetForm && form.value) {
        form.value.reset();
    }

    if (resetFields) {
        fields.value = clone(Schema);
    }

    bus('challenge:creation-flow:reseted', fields.value);
}

function recoverLastDraft(fn = () => {}) {
    if (Storage.has(CHALLENGE_DRAFT)) {
        nextTick(() => {
            const draft = Storage.get(CHALLENGE_DRAFT, defaultDraft);
            fields.value = draft.content;
            fn(fields.value);
            bus('challenge:creation-flow:recovered', fields.value);
        });
        return;
    }

    fn(fields.value);
}

async function removeDraft(id) {
    try {
        await api.drafts.remove(id);
        bus('challenge:creation-flow:reload-drafts');
    } catch (error) {
        console.error(error);
    }
}

async function redirect(data) {
    fields.value.challenge.slug = data.slug;

    track('Challenge Published', {
        challenge_id: data.id,
        challenge_slug: data.slug,
        platform_slug: contextPlatform.value?.slug,
        platform_type: contextPlatform.value?.type,
        solution_format: fields.value.challenge.solution_format,
        prize_type: fields.value.prize?.prize_type,
        has_prize_money: Number(fields.value.prize?.prize_money) > 0,
    });

    setTimeout(() => {
        const draft = Storage.get(CHALLENGE_DRAFT, defaultDraft);

        if (draft._id) {
            removeDraft(draft._id);
        }

        Storage.remove(CHALLENGE_DRAFT);
    }, 1000);

    // Hold the flow on the publish step until the bubble has emerged, been
    // read and flown to the bell. Moving on right away would hide the button
    // it is anchored to mid-animation.
    await publishBubble.earn();

    target.value.stepper.to('share');

    // First challenge only. The share step is the end of the flow, so the dash
    // is the last thing the player sees rather than something they have to get
    // past - and the neurons the publish just paid out are already in it.
    nudgeGameDash(NUDGE.FIRST_CHALLENGE, { delay: 1800 });
}

function normalize({ challenge: { innovation_scale } }) {
    fields.value.platform_domain = contextPlatform.value.id;
    fields.value.challenge.innovation_scale = innovation_scale || 'blue-sky';

    if (!String(fields.value.challenge.question_title).includes('?')) {
        fields.value.challenge.question_title = fields.value.challenge.question_title.trim() + '?';
    }

    if (isCaption.value) {
        return;
    }

    if (!get(fields.value, 'challenge.article.image_url')) {
        set(fields.value, 'challenge.article.image_url', fields.value.challenge.primary_image);
    }

    if (!fields.value.challenge.primary_image && get(fields.value, 'challenge.article.image_url')) {
        set(fields.value, 'challenge.primary_image', fields.value.challenge.article.image_url);
    }
}

function markAsSent(response) {
    if (fields.value.sent) {
        sentry.withScope(scope => {
            scope.setTag('step', 'challenge:creation-flow');
            scope.setContext('payload', fields.value);
            scope.setContext('response', response);
            sentry.captureMessage('Possible duplicate challenge submission', 'info');
        });
    }

    fields.value.sent = true;
}

// --- Debounced draft save ---

const debouncedSaveDraft = debounce((draft) => {
    if (!draft.origin) {
        draft.origin = 'challenge-creation-flow';
    }
    if (!draft.draft_type) {
        draft.draft_type = 'challenge';
    }

    bus('challenge:creation-flow:draft-updated', {
        draft,
        onDone: (savedDraft) => {
            if (savedDraft) {
                const localDraft = Storage.get(CHALLENGE_DRAFT, defaultDraft);
                localDraft._id = savedDraft._id || savedDraft.id;
                localDraft.origin = savedDraft.origin || 'challenge-creation-flow';
                localDraft.draft_type = savedDraft.draft_type || 'challenge';
                Storage.set(CHALLENGE_DRAFT, localDraft);
            }
        },
    });
}, 2000);

// --- Watchers ---

watch(fields, (newVal) => {
    const hasCategory = !!newVal.challenge.platform_category;
    const draft = Storage.get(CHALLENGE_DRAFT, defaultDraft);

    if (hasCategory) {
        draft.content = newVal;
        draft.platform_id = contextPlatform?.id;

        try {
            Storage.set(CHALLENGE_DRAFT, draft);
            debouncedSaveDraft(clone(draft));
        } catch (error) {
            throw error;
        }
    }
}, { deep: true });

watch(isCaption, (yes) => {
    fields.value.challenge.caption_solution_format = yes ? 'caption' : null;
});

watch(category, (cat) => {
    context.categorySelected = cat;
    context.categoryMain = categoryTree.value.length > 0 ? categoryTree.value[0] : null;
});

watch(() => route.fullPath, () => init());

// --- Bus listeners ---

on('challenge:creation-flow:step', (step) => {
    target.value?.stepper?.to(step);
});

on('challenge:creation-flow:restart', (step = 'category') => {
    target.value?.stepper?.to(step);
    init();
});

on('challenge:creation-flow:draft-selected', ({ draft } = {}) => {
    if (draft) {
        draft.content = clone(draft.content, Schema);
        fields.value = draft.content;
        Storage.set(CHALLENGE_DRAFT, draft);
        target.value?.stepper?.number(2);
    }
});

on('challenge:creation-flow:category-main', (cat) => {
    context.categoryMain = cat;
});

on('challenge:creation-flow:start-over', () => {
    reset({ form: true, store: true });
});

on('challenge:creation-flow:stepper:changed', ({ active }) => {
    if (!get(fields.value, `steps.${active}`)) {
        set(fields.value, `steps.${active}`, true);
    }
});

// --- Providers ---

provide('context', context);
provide('publishBubble', publishBubble);
provide('builder', reactive({
    get categoryTree() { return categoryTree.value; },
    set categoryTree(val) { categoryTree.value = val; },
    get isCaption() { return isCaption.value; },
    get isQQ() { return isQQ.value; },
    get isTestIdea() { return isTestIdea.value; },
    get category() { return category.value; },
    usingTool,
}));

// --- Init ---

init();
</script>

<template>
    <div :class="{ 'disabled': !abilities?.can('create', 'game.challenge') }">
        <SubplatformNotice>
            <div class="flex items-center justify-center sm:bg-secondary-500 rounded-md sm:px-4 py-2">
                <h1 class="m-0 text-secondary sm:text-white">
                    <NIcon as="question-circle" class="mr-2" />{{ $t('pages.challenges.create.title') }}
                </h1>
            </div>
        </SubplatformNotice>

        <!-- <init></init> -->
        <NDataForm ref="form" action="/challenges" :data="fields" :dirtable="dirtable" once @before-send="normalize" @after-send="markAsSent" @success="redirect">
            <QqForm v-if="usingTool('qq')" ref="target" />
            <TestIdeaForm v-else-if="usingTool('test-an-idea')" ref="target" />
            <ImproveIdeaForm v-else-if="usingTool('improve-an-idea')" ref="target" />
            <ChallengeForm v-else ref="target" />
        </NDataForm>
    </div>
</template>
